import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { Layout, Spin, message, ConfigProvider } from 'antd';
import enUS from 'antd/lib/locale/en_US';
import PrivateRoute from './routing/PrivateRoute';
import TopNav from './layout/TopNav';
import Footer from './layout/Footer';
import Login from './login/Login';
import Home from './home/Home';
import Projects from './projects/Projects';
import Profile from './project/profile/Profile';
import EditProfile from './project/profile/EditProfile';
import Extensions from './project/extension/Extensions';
import AddExtension from './project/extension/AddExtension';
import Extension from './project/extension/Extension';
import SystemSetting from './systemSetting/SystemSetting';
import Logout from './logout/Logout';
import PendingApprovals from './pendingApprovals';
import AnnualReports from './annualReports';
import AnnualReport from './annualReports/AnnualReport';
import AddAnnualReport from './annualReports/AddAnnualReport';
import { Team, AddTeam, EditTeam } from './project/team';
import {
  AddBudget,
  AddVirement,
  Budget,
  BudgetVirement,
  EditBudget,
  Virement,
} from './project/budgetVirement';
import Termination from './project/termination';
import { AddWithdrawal, Withdrawal, Withdrawals } from './project/withdrawal';
import {
  AddProposalRevision,
  ProposalRevision,
  ProposalRevisions,
} from './project/proposalRevision';
import {
  AddReportSubmission,
  ReportSubmission,
  ReportSubmissions,
  EditReportSubmission,
} from './project/reportSubmission';
import UserProfile from './userProfile/UserProfile';
// TODO: pass role module
// import { AddPassRole, PassRole, EditPassRole } from './passRoles';
import * as ErrorPage from './layout/ErrorPage';
import { SCOPE_TYPES } from 'constants/scopes';
import {
  OtherReports,
  BulkDownload,
  FundingApplications,
} from './otherReports';
import EditAnnualReport from './annualReports/EditAnnualReport';
import {
  ProjectApplications,
  ApplyProjectApplication,
  ProjectApplication,
  EditProjectApplicationAfterward,
} from 'components/projectApplications';
import IdelTimer from './auth/IdelTimer';

const { Content } = Layout;
const initialState = { loading: false };
export const LoadingReducerContext = React.createContext(null);

// for useLoading hooks to show loading spin globally
const loadingReducer = (_state, action) => {
  switch (action.type) {
    case 'START_LOADING':
      return {
        loading: true,
      };
    case 'END_LOADING':
      return {
        loading: false,
      };
    default:
      return initialState;
  }
};

const Pages = () => {
  const [state, dispatch] = React.useReducer(loadingReducer, initialState);
  message.config({
    duration: 1,
    maxCount: 3,
  });

  return (
    <LoadingReducerContext.Provider value={{ state, dispatch }}>
      <ConfigProvider locale={enUS}>
        <Spin spinning={state.loading} tip="Loading..." delay={50}>
          <Layout style={{ minHeight: '100vh' }}>
            <Switch>
              <Route exact path="/login" component={Login} />
              <Route exact path="/logout" component={Logout} />
              <Route
                exact
                path="/session-timeout"
                component={ErrorPage.SessionTimeout}
              />
              <Route
                path="/user-no-role"
                exact
                component={ErrorPage.UserNoRole}
              />
              <Route
                exact
                path="/user-not-found"
                component={ErrorPage.UserNotFound}
              />
              <Route
                exact
                path="/user-department-not-found"
                component={ErrorPage.UserDepartmentNotFound}
              />
              <Route
                exact
                path="/user-disabled"
                component={ErrorPage.UserDisabled}
              />
              <Route path="/">
                <IdelTimer />
                <PrivateRoute path="/" component={TopNav} />
                <Content>
                  <Switch>
                    <Route exact path="/">
                      <Redirect to="/home" />
                    </Route>
                    <PrivateRoute exact path="/home" component={Home} />
                    <PrivateRoute exact path="/projects" component={Projects} />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/profile"
                      component={Profile}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/profile/edit"
                      component={EditProfile}
                      expectedScopeType={[SCOPE_TYPES.WRITE_PROJECT]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/team"
                      component={Team}
                      expectedScopeTypeInProject={[SCOPE_TYPES.VIEW_TEAM]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/team/add"
                      component={AddTeam}
                      expectedScopeTypeInProject={[SCOPE_TYPES.WRITE_TEAM]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/team/:id/edit"
                      component={EditTeam}
                      expectedScopeTypeInProject={[SCOPE_TYPES.WRITE_TEAM]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/budget-virement"
                      component={BudgetVirement}
                      expectedScopeTypeInProject={[
                        SCOPE_TYPES.VIEW_BUDGET,
                        SCOPE_TYPES.VIEW_VIREMENT,
                      ]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/budget-virement/budgets/add"
                      component={AddBudget}
                      expectedScopeTypeInProject={[SCOPE_TYPES.WRITE_BUDGET]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/budget-virement/budgets/:id"
                      component={Budget}
                      expectedScopeTypeInProject={[SCOPE_TYPES.VIEW_BUDGET]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/budget-virement/budgets/:id/edit"
                      component={EditBudget}
                      expectedScopeTypeInProject={[SCOPE_TYPES.WRITE_BUDGET]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/budget-virement/virements/add"
                      component={AddVirement}
                      expectedScopeTypeInProject={[
                        SCOPE_TYPES.WRITE_VIREMENT,
                        SCOPE_TYPES.WRITE_VIREMENT_WITH_CUSTOM_DATE,
                      ]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/budget-virement/virements/:id"
                      component={Virement}
                      expectedScopeTypeInProject={[SCOPE_TYPES.VIEW_VIREMENT]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/report-submissions"
                      component={ReportSubmissions}
                      expectedScopeTypeInProject={[
                        SCOPE_TYPES.VIEW_REPORT_SUBMISSION,
                      ]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/report-submissions/add"
                      component={AddReportSubmission}
                      expectedScopeTypeInProject={[
                        SCOPE_TYPES.WRITE_SCHEDULED_REPORT_SUBMISSION,
                      ]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/report-submissions/:id"
                      component={ReportSubmission}
                      expectedScopeTypeInProject={[
                        SCOPE_TYPES.VIEW_REPORT_SUBMISSION,
                      ]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/report-submissions/:id/edit"
                      component={EditReportSubmission}
                      expectedScopeTypeInProject={[
                        SCOPE_TYPES.WRITE_SCHEDULED_REPORT_SUBMISSION,
                      ]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/extensions"
                      component={Extensions}
                      expectedScopeTypeInProject={[SCOPE_TYPES.VIEW_EXTENSION]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/extensions/add"
                      component={AddExtension}
                      expectedScopeTypeInProject={[
                        SCOPE_TYPES.WRITE_EXTENSION,
                        SCOPE_TYPES.WRITE_EXTENSION_WITH_CUSTOM_DATE,
                      ]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/extensions/:id"
                      component={Extension}
                      expectedScopeTypeInProject={[SCOPE_TYPES.VIEW_EXTENSION]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/proposal-revisions"
                      component={ProposalRevisions}
                      expectedScopeTypeInProject={[
                        SCOPE_TYPES.VIEW_PROPOSAL_REVISION,
                      ]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/proposal-revisions/add"
                      component={AddProposalRevision}
                      expectedScopeTypeInProject={[
                        SCOPE_TYPES.WRITE_PROPOSAL_REVISION,
                        SCOPE_TYPES.WRITE_PROPOSAL_REVISION_WITH_CUSTOM_DATE,
                      ]}
                    />

                    <PrivateRoute
                      exact
                      path="/projects/:projectId/proposal-revisions/:id"
                      component={ProposalRevision}
                      expectedScopeTypeInProject={[
                        SCOPE_TYPES.VIEW_PROPOSAL_REVISION,
                      ]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/withdrawals"
                      component={Withdrawals}
                      expectedScopeTypeInProject={[SCOPE_TYPES.VIEW_WITHDRAWAL]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/withdrawals/add"
                      component={AddWithdrawal}
                      expectedScopeTypeInProject={[
                        SCOPE_TYPES.WRITE_WITHDRAWAL,
                        SCOPE_TYPES.WRITE_WITHDRAWAL_WITH_CUSTOM_DATE,
                      ]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/withdrawals/:id"
                      component={Withdrawal}
                      expectedScopeTypeInProject={[SCOPE_TYPES.VIEW_WITHDRAWAL]}
                    />
                    <PrivateRoute
                      exact
                      path="/projects/:projectId/termination"
                      component={Termination}
                      expectedScopeTypeInProject={[
                        SCOPE_TYPES.VIEW_TERMINATION,
                        SCOPE_TYPES.WRITE_TERMINATION,
                        SCOPE_TYPES.WRITE_TERMINATION_WITH_CUSTOM_DATE,
                      ]}
                    />
                    <PrivateRoute
                      path="/projects/:projectId"
                      component={ErrorPage.Error404}
                    />
                    <PrivateRoute
                      exact
                      path="/pending-approvals"
                      component={PendingApprovals}
                    />
                    <PrivateRoute
                      exact
                      path="/annual-reports"
                      expectedScopeType={[
                        SCOPE_TYPES.WRITE_ANNUAL_REPORT,
                        SCOPE_TYPES.WRITE_ANNUAL_REPORT_WITH_CUSTOM_DATE,
                        SCOPE_TYPES.VIEW_ANNUAL_REPORT,
                        SCOPE_TYPES.VIEW_ALL_ANNUAL_REPORT,
                        SCOPE_TYPES.VIEW_ANNUAL_REPORT_PROJECT,
                      ]}
                      component={AnnualReports}
                    />
                    <PrivateRoute
                      exact
                      path="/annual-reports/add"
                      component={AddAnnualReport}
                      expectedScopeType={[
                        SCOPE_TYPES.WRITE_ANNUAL_REPORT,
                        SCOPE_TYPES.WRITE_ANNUAL_REPORT_WITH_CUSTOM_DATE,
                      ]}
                    />

                    <PrivateRoute
                      exact
                      path="/annual-reports/:id/edit"
                      component={EditAnnualReport}
                      expectedScopeType={[
                        SCOPE_TYPES.WRITE_ANNUAL_REPORT,
                        SCOPE_TYPES.WRITE_ANNUAL_REPORT_WITH_CUSTOM_DATE,
                      ]}
                    />
                    <PrivateRoute
                      exact
                      path="/annual-reports/:id"
                      component={AnnualReport}
                      expectedScopeType={[
                        SCOPE_TYPES.VIEW_ANNUAL_REPORT,
                        SCOPE_TYPES.VIEW_ALL_ANNUAL_REPORT,
                      ]}
                    />
                    <PrivateRoute
                      exact
                      path="/user-profile"
                      component={UserProfile}
                    />
                    {/* TODO: pass role module
                  <PrivateRoute
                    exact
                    path="/pass-roles/add"
                    component={AddPassRole}
                  />
                  <PrivateRoute
                    exact
                    path="/pass-roles/:id"
                    component={PassRole}
                  />
                  <PrivateRoute
                    exact
                    path="/pass-roles/:id/edit"
                    component={EditPassRole}
                  /> */}
                    <PrivateRoute
                      exact
                      path="/project-applications/apply"
                      component={ApplyProjectApplication}
                    />
                    <PrivateRoute
                      exact
                      path={[
                        '/project-applications/my',
                        '/project-applications/fundings',
                        '/project-applications/all',
                      ]}
                      component={ProjectApplications}
                    />
                    <PrivateRoute
                      exact
                      path="/project-applications/:id"
                      component={ProjectApplication}
                    />

                    <PrivateRoute
                      exact
                      path="/project-applications/:id/edit"
                      component={EditProjectApplicationAfterward}
                    />
                    <PrivateRoute
                      exact
                      path="/other-reports"
                      component={OtherReports}
                      expectedScopeType={[SCOPE_TYPES.VIEW_OTHER_REPORTS]}
                    />
                    <PrivateRoute
                      exact
                      path="/other-reports/bulk-download"
                      component={BulkDownload}
                      expectedScopeType={[SCOPE_TYPES.VIEW_OTHER_REPORTS]}
                    />
                    <PrivateRoute
                      exact
                      path="/other-reports/funding-applications"
                      component={FundingApplications}
                      expectedScopeType={[SCOPE_TYPES.VIEW_OTHER_REPORTS]}
                    />
                    <PrivateRoute
                      path="/system-setting"
                      component={SystemSetting}
                      expectedScopeType={[
                        // TODO: add all scope types
                        SCOPE_TYPES.WRITE_APPROVAL_ROUTE_TEMPLATE,
                        SCOPE_TYPES.WRITE_BUDGET_CATEGORY,
                      ]}
                    />
                    <Route component={ErrorPage.Error404} />
                  </Switch>
                </Content>
              </Route>
            </Switch>
            <Footer />
          </Layout>
        </Spin>
      </ConfigProvider>
    </LoadingReducerContext.Provider>
  );
};

export default Pages;
